/**
 * ContractsSection — Displays contracts on the Earnings page.
 * Shows day rate and salaried contracts with status, progress, and actions.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Plus, Edit, Trash2, Briefcase, PoundSterling, Calendar, FileCheck, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import ContractDialog from '../../components/contracts/ContractDialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  completed: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  p45: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const ContractCard = ({ contract, onEdit, onDelete }) => {
  const isSalaried = contract.contract_type === 'salaried';
  const daysProgress = isSalaried && contract.contract_days
    ? Math.min(100, (contract.cumulative_days_worked / contract.contract_days) * 100)
    : 0;
  const isPostContract = isSalaried && contract.cumulative_days_worked > (contract.contract_days || 0);

  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600 transition-colors" data-testid={`contract-card-${contract.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="h-4 w-4 text-amber-400 flex-shrink-0" />
            <h4 className="text-sm font-semibold text-white truncate">{contract.employer_name}</h4>
            {contract.client_name && (
              <span className="text-xs text-slate-400 truncate">/ {contract.client_name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[10px] ${statusColors[contract.status] || statusColors.active}`}>
              {contract.status === 'p45' ? 'P45' : contract.status}
            </Badge>
            <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400">
              {isSalaried ? 'Salaried' : 'Day Rate'}
            </Badge>
            {contract.linked_trip_count > 0 && (
              <span className="text-[10px] text-slate-500">{contract.linked_trip_count} trip{contract.linked_trip_count !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onEdit(contract)} className="h-7 w-7 p-0 text-slate-400 hover:text-white" data-testid="contract-edit-btn">
            <Edit className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(contract)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" data-testid="contract-delete-btn">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Dates */}
      <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
        <Calendar className="h-3 w-3" />
        <span>{format(parseISO(contract.start_date), 'dd MMM yyyy')} — {format(parseISO(contract.end_date), 'dd MMM yyyy')}</span>
        {contract.p45_date && (
          <span className="text-amber-400 ml-1">(P45: {format(parseISO(contract.p45_date), 'dd MMM yyyy')})</span>
        )}
      </div>

      {/* Rate info */}
      {!isSalaried && (
        <div className="flex items-center gap-1.5 text-sm">
          <PoundSterling className="h-3.5 w-3.5 text-amber-400" />
          <span className="text-white font-medium">£{contract.daily_rate?.toLocaleString('en-GB')}/day</span>
        </div>
      )}

      {isSalaried && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-slate-400">Monthly base</span>
              <p className="text-white font-medium">£{contract.monthly_base?.toLocaleString('en-GB')}</p>
            </div>
            <div>
              <span className="text-slate-400">Uplift</span>
              <p className="text-white font-medium">
                £{contract.daily_uplift?.toLocaleString('en-GB')}/day
                {isPostContract && (
                  <span className="text-emerald-400 ml-1">(now £{contract.post_contract_uplift?.toLocaleString('en-GB')})</span>
                )}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-[10px]">
              <span className="text-slate-400">
                {contract.cumulative_days_worked}/{contract.contract_days} contract days
              </span>
              {isPostContract && (
                <span className="text-emerald-400 font-medium flex items-center gap-0.5">
                  <FileCheck className="h-3 w-3" /> Contract fulfilled
                </span>
              )}
            </div>
            <Progress value={daysProgress} className="h-1.5 bg-slate-700" />
          </div>
        </div>
      )}

      {contract.p45_earnings && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center gap-1.5 text-xs">
          <AlertTriangle className="h-3 w-3 text-amber-400" />
          <span className="text-slate-400">P45 Earnings:</span>
          <span className="text-white font-medium">£{contract.p45_earnings?.toLocaleString('en-GB')}</span>
        </div>
      )}
    </div>
  );
};

const ContractsSection = ({ contracts: propContracts, onRefresh: propRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [localContracts, setLocalContracts] = useState([]);

  // Self-sufficient: fetch own data when used as a standalone tab
  const fetchContracts = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contracts`);
      setLocalContracts(res.data.contracts || []);
    } catch (err) {
      console.error('Failed to load contracts:', err);
    }
  }, []);

  React.useEffect(() => {
    if (!propContracts) fetchContracts();
  }, [propContracts, fetchContracts]);

  const contracts = propContracts || localContracts;
  const onRefresh = propRefresh || fetchContracts;

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingContract(null);
    setDialogOpen(true);
  };

  const handleDelete = async (contract) => {
    if (deleteConfirm?.id === contract.id) {
      try {
        await axios.delete(`${API_URL}/api/contracts/${contract.id}`);
        toast.success('Contract deleted');
        setDeleteConfirm(null);
        onRefresh();
      } catch (err) {
        toast.error('Failed to delete contract');
      }
    } else {
      setDeleteConfirm(contract);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  return (
    <>
      <Card className="bg-slate-800/40 border-slate-700/50" data-testid="contracts-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-amber-400" />
              Contracts
            </CardTitle>
            <Button
              size="sm"
              onClick={handleAdd}
              className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs"
              data-testid="add-contract-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Contract
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <div className="text-center py-6">
              <Briefcase className="h-8 w-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No contracts yet</p>
              <p className="text-xs text-slate-500 mt-1">Add your employment contracts to track earnings accurately</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map(contract => (
                <ContractCard
                  key={contract.id}
                  contract={contract}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {deleteConfirm && (
            <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30 text-xs text-red-400">
              Click delete again to confirm removing <strong>{deleteConfirm.employer_name}</strong>. Linked trips will be unlinked.
            </div>
          )}
        </CardContent>
      </Card>

      <ContractDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contract={editingContract}
        onSaved={onRefresh}
      />
    </>
  );
};

export default ContractsSection;
